/**
 * Push Notification utility for Hana PWA
 * Handles subscription to Web Push notifications.
 */
import api from "@/api";

/**
 * Convert a URL-safe base64 VAPID key to a Uint8Array for the Push API.
 */
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Subscribe the current user to push notifications.
 * Returns true if successful, false otherwise.
 */
export async function subscribeToPush() {
  try {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
      console.log("Push not supported");
      return false;
    }

    // Get VAPID public key from backend
    const keyRes = await api.get("/push/vapid-public-key");
    const vapidPublicKey = keyRes.data.publicKey;
    if (!vapidPublicKey) {
      console.log("No VAPID key configured");
      return false;
    }

    // Request notification permission
    const permission = await Notification.requestPermission();
    if (permission !== "granted") {
      console.log("Notification permission denied");
      return false;
    }

    // Get service worker registration
    const registration = await navigator.serviceWorker.ready;

    // Reuse an existing browser subscription when present, then re-sync it server-side.
    // This prevents a common production issue: the browser is subscribed, but the
    // backend DB lost/never received the endpoint after a redeploy or domain change.
    let subscription = await registration.pushManager.getSubscription();
    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
      });
    }

    // Send subscription to backend
    const subJSON = subscription.toJSON();
    await api.post("/push/subscribe", {
      endpoint: subJSON.endpoint,
      keys: subJSON.keys,
      expirationTime: subJSON.expirationTime || null,
    });

    console.log("Push subscription successful");
    return true;
  } catch (err) {
    console.error("Push subscription failed:", err);
    return false;
  }
}

/**
 * Check if push notifications are already subscribed on the browser side.
 */
export async function isPushSubscribed() {
  try {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) return false;
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    return !!subscription;
  } catch {
    return false;
  }
}

/**
 * Check backend state (DB source of truth).
 */
export async function getBackendPushStatus() {
  try {
    const res = await api.get("/push/status");
    return { subscribed: !!res.data.subscribed, endpoints: res.data.endpoints || 0 };
  } catch {
    return { subscribed: false, endpoints: 0 };
  }
}

/**
 * Unsubscribe from push on the browser AND notify backend.
 */
export async function unsubscribeFromPush() {
  try {
    if (!("serviceWorker" in navigator)) return true;
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    if (subscription) {
      try {
        await api.post("/push/unsubscribe", { endpoint: subscription.endpoint });
      } catch {}
      await subscription.unsubscribe();
    } else {
      // Still tell backend to clean any stale server-side endpoints
      try { await api.post("/push/unsubscribe", { endpoint: "" }); } catch {}
    }
    return true;
  } catch (err) {
    console.error("Unsubscribe failed:", err);
    return false;
  }
}

/**
 * Send a test push to the current user via backend.
 */
export async function sendTestPush() {
  try {
    const res = await api.post("/push/test");
    return res.data;
  } catch (err) {
    return { status: "error", message: err.response?.data?.message || "Erreur" };
  }
}
